import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import OpenSeadragon from 'openseadragon'
import {
  api,
  type EditionBrief,
  type EditionManifest,
  type EditionStats,
  type PageDetail,
} from '../lib/api'
import { Chevron } from '../components/Page'
import { Glyph } from '../components/viewer/Glyph'
import { PageWell, type WellHandle } from '../components/viewer/PageWell'
import {
  geometryLine,
  geometryOf,
  parseRegionParam,
  pageLabel,
  type PageGeometry,
} from '../components/viewer/model'

/**
 * Two pages side by side.
 *
 * Two eras of the same title have different page geometry: a 1990 broadsheet
 * is wider than a 2026 one and carries six columns against five. Locked zoom
 * therefore scales to the printed sheet in points, not to the raster, so a
 * column in 1990 and a column in 2026 read at the same physical size. Locking
 * the rasters instead would compare scanner settings rather than newspapers.
 */

function useSide(pageId: string | null) {
  const page = useQuery({
    queryKey: ['page', pageId],
    queryFn: () => api.get<PageDetail>(`/pages/${pageId}`),
    enabled: !!pageId,
    retry: false,
  })
  const manifest = useQuery({
    queryKey: ['edition', page.data?.edition_id],
    queryFn: () => api.get<EditionManifest>(`/editions/${page.data!.edition_id}`),
    enabled: !!page.data?.edition_id,
  })

  const detail: PageDetail | null = page.data
    ? page.data.edition
      ? page.data
      : manifest.data
        ? {
            ...page.data,
            edition: {
              id: manifest.data.id,
              pub_date: manifest.data.pub_date,
              edition_label: manifest.data.edition_label,
              route: manifest.data.route ?? (page.data.source_type === 'digital' ? 'A' : 'B'),
              page_count: manifest.data.page_count,
              publication_name: manifest.data.publication_name,
              publication_slug: manifest.data.publication_slug,
              column_count: manifest.data.column_count,
              column_pitch_pt: manifest.data.column_pitch_pt,
              page_w_pt: manifest.data.page_w_pt ?? null,
              page_h_pt: manifest.data.page_h_pt ?? null,
            },
          }
        : page.data
    : null

  return {
    detail,
    manifest: manifest.data ?? null,
    geometry: detail ? geometryOf(detail) : null,
    stats: manifest.data?.stats ?? null,
  }
}

export function ComparePage() {
  const [params, setParams] = useSearchParams()
  const left = params.get('left')
  const right = params.get('right')
  const [locked, setLocked] = useState(true)
  const [picking, setPicking] = useState(false)
  const [leftWell, setLeftWell] = useState<WellHandle | null>(null)
  const [rightWell, setRightWell] = useState<WellHandle | null>(null)

  const a = useSide(left)
  const b = useSide(right)

  const editions = useQuery({
    queryKey: ['editions'],
    queryFn: () => api.get<EditionBrief[]>('/editions'),
  })

  useEffect(() => {
    if (!left || !right) setPicking(true)
  }, [left, right])

  /* Locked zoom is a scale in points, so the two rasters are never compared
     directly: the same printed inch is the same number of screen pixels on
     both sheets whatever ppi each was captured at. */
  useEffect(() => {
    const one = leftWell?.viewer
    const two = rightWell?.viewer
    const geoOne = a.geometry
    const geoTwo = b.geometry
    if (!locked || !one || !two || !geoOne || !geoTwo) return
    if (!geoOne.widthPt || !geoTwo.widthPt) return
    let syncing = false

    const mirror =
      (from: OpenSeadragon.Viewer, to: OpenSeadragon.Viewer, fromPt: number, toPt: number) =>
      () => {
        if (syncing) return
        syncing = true
        try {
          const zoom = from.viewport.getZoom()
          const centre = from.viewport.getCenter()
          to.viewport.zoomTo((zoom * toPt) / fromPt, undefined, true)
          to.viewport.panTo(
            new OpenSeadragon.Point((centre.x * fromPt) / toPt, (centre.y * fromPt) / toPt),
            true,
          )
        } finally {
          syncing = false
        }
      }

    const leftToRight = mirror(one, two, geoOne.widthPt, geoTwo.widthPt)
    const rightToLeft = mirror(two, one, geoTwo.widthPt, geoOne.widthPt)

    one.addHandler('zoom', leftToRight)
    one.addHandler('pan', leftToRight)
    two.addHandler('zoom', rightToLeft)
    two.addHandler('pan', rightToLeft)
    leftToRight()
    return () => {
      one.removeHandler('zoom', leftToRight)
      one.removeHandler('pan', leftToRight)
      two.removeHandler('zoom', rightToLeft)
      two.removeHandler('pan', rightToLeft)
    }
  }, [locked, leftWell, rightWell, a.geometry, b.geometry])

  const swap = () => {
    const next = new URLSearchParams(params)
    if (left) next.set('right', left)
    else next.delete('right')
    if (right) next.set('left', right)
    else next.delete('left')
    const lr = params.get('lr')
    const rr = params.get('rr')
    if (rr) next.set('lr', rr)
    else next.delete('lr')
    if (lr) next.set('rr', lr)
    else next.delete('rr')
    setParams(next, { replace: true })
  }

  const label = (geometry: PageGeometry | null, detail: PageDetail | null) =>
    geometry && detail
      ? `${geometry.pubDate} · ${pageLabel(detail.section_label, detail.page_number)}`
      : 'not chosen'

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex flex-none flex-wrap items-center gap-2.5 border-b border-n300 px-4 py-2.5">
        <div className="font-mono text-[11px]">{label(a.geometry, a.detail)}</div>
        <span className="text-ink3">
          <Glyph name="swap" size={13} />
        </span>
        <div className="font-mono text-[11px]">{label(b.geometry, b.detail)}</div>

        <div className="ml-auto flex items-center gap-2.5">
          <button
            type="button"
            role="switch"
            aria-checked={locked}
            onClick={() => setLocked((value) => !value)}
            className="flex items-center gap-2 text-[13px]"
          >
            <span
              className="flex h-[15px] w-[15px] items-center justify-center rounded-[--radius-md] border"
              style={{
                borderColor: 'var(--jl-accent)',
                background: locked ? 'var(--jl-accent)' : 'transparent',
                color: 'var(--color-bg)',
              }}
            >
              {locked && <Glyph name="check" size={10} strokeWidth={2} />}
            </span>
            Lock zoom &amp; pan
          </button>
          <button type="button" className="btn-secondary px-2.5 py-1" onClick={swap}>
            Swap
          </button>
          <button
            type="button"
            className="btn-secondary px-2.5 py-1"
            aria-expanded={picking}
            onClick={() => setPicking((value) => !value)}
          >
            Change
          </button>
        </div>
      </div>

      {picking && (
        <div className="flex flex-none flex-wrap items-center gap-4 border-b border-n300 bg-surface px-4 py-2.5">
          <SidePicker
            side="left"
            label="Left"
            editions={editions.data ?? []}
            currentPageId={left}
            onPick={(id) => {
              const next = new URLSearchParams(params)
              next.set('left', id)
              setParams(next, { replace: true })
            }}
          />
          <SidePicker
            side="right"
            label="Right"
            editions={editions.data ?? []}
            currentPageId={right}
            onPick={(id) => {
              const next = new URLSearchParams(params)
              next.set('right', id)
              setParams(next, { replace: true })
            }}
          />
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-2 gap-px bg-rule2">
        <Pane
          detail={a.detail}
          geometry={a.geometry}
          regionParam={params.get('lr')}
          onWell={setLeftWell}
        />
        <Pane
          detail={b.detail}
          geometry={b.geometry}
          regionParam={params.get('rr')}
          onWell={setRightWell}
        />
      </div>

      <div className="grid flex-none grid-cols-2 gap-px border-t border-n300 bg-n300">
        <GeometryCell geometry={a.geometry} detail={a.detail} stats={a.stats} />
        <GeometryCell geometry={b.geometry} detail={b.detail} stats={b.stats} />
      </div>

      <div className="flex-none border-t border-n300 px-4 py-3">
        <p className="max-w-[86ch] text-[15px] leading-normal text-ink2">
          {a.geometry && b.geometry && a.geometry.widthPt !== b.geometry.widthPt ? (
            <>
              These two sheets have different page geometry: {Math.round(a.geometry.widthPt)} points
              wide against {Math.round(b.geometry.widthPt)}, {a.geometry.columnCount || '?'} columns
              against {b.geometry.columnCount || '?'}. Locked zoom therefore scales to the printed
              sheet rather than to the raster, so a column in {a.geometry.pubDate?.slice(0, 4)} and a
              column in {b.geometry.pubDate?.slice(0, 4)} read at the same physical size.
            </>
          ) : (
            <>
              Locked zoom scales to the printed sheet in points, not to the raster. Two editions
              captured at different resolutions therefore read at the same physical size, and a
              column is a column in either year.
            </>
          )}
        </p>
      </div>
    </div>
  )
}

function Pane({
  detail,
  geometry,
  regionParam,
  onWell,
}: {
  detail: PageDetail | null
  geometry: PageGeometry | null
  regionParam: string | null
  onWell: (handle: WellHandle) => void
}) {
  if (!detail || !geometry) {
    return (
      <div className="flex min-h-0 items-center justify-center bg-well p-6">
        <p
          className="max-w-[36ch] text-center text-[14px] leading-normal"
          style={{ color: 'color-mix(in srgb, white 66%, var(--jl-well))' }}
        >
          Nothing chosen for this side yet. Use Change above, or open a page and press C.
        </p>
      </div>
    )
  }
  return (
    <PageWell
      pageId={detail.id}
      geometry={geometry}
      regions={[]}
      visibleTypes={new Set()}
      overlayOn
      selectedKey={null}
      selectionBox={parseRegionParam(regionParam)}
      onSelect={() => undefined}
      hits={[]}
      activeHit={0}
      selecting={false}
      onMarquee={() => undefined}
      onReady={onWell}
      showNavigator={false}
    />
  )
}

function GeometryCell({
  geometry,
  detail,
  stats,
}: {
  geometry: PageGeometry | null
  detail: PageDetail | null
  stats: EditionStats | null
}) {
  if (!geometry || !detail) {
    return <div className="bg-surface px-4 py-2.5 font-mono text-[10.5px] text-ink3">-</div>
  }
  return (
    <div className="bg-surface px-4 py-2.5 font-mono text-[10.5px] leading-[1.75]">
      <div>{geometryLine(geometry)}</div>
      <div className="text-ink2">
        {[
          geometry.route ? `route ${geometry.route}` : null,
          geometry.engine,
          geometry.pageCount ? `${geometry.pageCount} pp` : null,
          stats?.mean_confidence !== undefined && stats?.mean_confidence !== null
            ? `${(stats.mean_confidence * 100).toFixed(1)}% mean`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  )
}

function SidePicker({
  side,
  label,
  editions,
  currentPageId,
  onPick,
}: {
  side: 'left' | 'right'
  label: string
  editions: EditionBrief[]
  currentPageId: string | null
  onPick: (pageId: string) => void
}) {
  const [editionId, setEditionId] = useState<string>('')
  const manifest = useQuery({
    queryKey: ['edition', editionId],
    queryFn: () => api.get<EditionManifest>(`/editions/${editionId}`),
    enabled: !!editionId,
  })

  return (
    <div className="flex items-center gap-2 text-[13px]">
      <span className="chrome">{label}</span>
      <select
        className="input w-auto py-1 text-[13px]"
        aria-label={`Edition for the ${side} pane`}
        value={editionId}
        onChange={(event) => setEditionId(event.target.value)}
      >
        <option value="">Choose an edition</option>
        {editions.map((edition) => (
          <option key={edition.id} value={edition.id}>
            {edition.pub_date} · {edition.source_type}
          </option>
        ))}
      </select>
      <select
        className="input w-auto py-1 text-[13px]"
        aria-label={`Page for the ${side} pane`}
        value={currentPageId ?? ''}
        onChange={(event) => onPick(event.target.value)}
        disabled={!manifest.data}
      >
        <option value="">Choose a page</option>
        {(manifest.data?.pages ?? []).map((page) => (
          <option key={page.id} value={page.id}>
            {pageLabel(page.section_label, page.page_number)}
          </option>
        ))}
      </select>
      <Link to="/browse" className="btn-ghost text-[13px]">
        Browse
        <Chevron size={11} />
      </Link>
    </div>
  )
}
